import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { usePartialPublishEventDetails } from 'screens/performanceReview/helpers';
import { addPluralSuffixS } from 'utils/helpers/stringHelpers';

import { CheckCircle, ErrorOutline } from 'assets/assetsV1/iconComponents';

import { KH_LINKS } from 'constants/knowledgeHubLinks';
import { sendPPShareResultsKBaseEvent } from 'constants/mixpanelHelpers/partialPublishEvents';
import { isNetchexLogin } from 'constants/netChexConstants';

import { DrawerBody, DrawerFooter, DrawerHeaderSecondary } from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { InfiniteProgressBar } from 'uiToolkitV1/InfiniteProgressBar';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { usePartialPublishContext } from './PartialPublishContext';
import { useIsPublishingInProgress, useSubmitPartialPublishMutation } from './dataHooks';

const Loading = () => (
  <Box bgColor="white" p={4}>
    <InfiniteProgressBar />
  </Box>
);

const SuccessScreen = () => {
  const { selectedSection } = usePartialPublishContext();

  const message =
    selectedSection.section === 'ALLOW_MANAGER_PUBLISH'
      ? `Successfully shared results of ${selectedSection?.employeeStatusList.length} Employee${addPluralSuffixS(
          selectedSection?.employeeStatusList.length
        )} with their managers!`
      : `Successfully shared results with ${selectedSection?.employeeStatusList.length} Employee${addPluralSuffixS(
          selectedSection?.employeeStatusList.length
        )}!`;

  return (
    <Flex flexDir="column" mx={5} py={5} pt={8}>
      <Flex
        flexDir="column"
        bg="radial-gradient(299.07% 219.61% at 50% 50%, rgba(219, 242, 211, 0.00) 0%, #DBF2D3 97.92%)"
        p="30px 16px 40px"
        borderRadius="8px"
        alignItems="center"
        gap={3}
        border="1px solid"
        borderColor="border.EBEFF3"
      >
        <CheckCircle color="functional.44812F" boxSize="92px" />
        <TextBodyMedium bold color="functional.44812F">
          {message}
        </TextBodyMedium>
      </Flex>
    </Flex>
  );
};

const FailureScreen = () => (
  <Flex flexDir="column" mx={5} py={5} pt={8}>
    <Flex
      flexDir="column"
      bg="radial-gradient(299.07% 219.61% at 50% 50%, rgba(242, 213, 211, 0.00) 0%, #F2D5D3 97.92%)"
      p="30px 16px 40px"
      borderRadius="8px"
      alignItems="center"
      border="1px solid"
      borderColor="border.EBEFF3"
      gap={3}
    >
      <ErrorOutline color="functional.C62C02" boxSize="92px" />
      <TextBodyMedium bold color="functional.C62C02">
        Oops! We couldn’t publish your reviews. How about giving it another try?
      </TextBodyMedium>
    </Flex>
  </Flex>
);

const Result = ({ onClose }) => {
  const isLoading = useIsPublishingInProgress();
  const { publishSuccessful } = usePartialPublishContext();
  const { mutate: submitPartialPublish } = useSubmitPartialPublishMutation();

  const { module, empId, page } = usePartialPublishEventDetails();

  const handleClick = () => {
    if (publishSuccessful) {
      onClose();
    } else {
      submitPartialPublish();
    }
  };

  if (!isLoading && publishSuccessful === null) {
    return null;
  }

  const sendKBaseEvent = () => {
    sendPPShareResultsKBaseEvent({
      eventProperties: {
        page,
        source: module,
        empId,
        shareKb: true,
      },
    });
  };

  return (
    <>
      <DrawerHeaderSecondary onClose={onClose}>
        <Flex
          alignItems="center"
          borderBottom="1px"
          borderBottomColor="neutral.CFD7DF"
          mx={4}
          py={5}
          justifyContent="space-between"
        >
          <Flex flexDir="column">
            <TextBodyMedium bold>Sharing Results</TextBodyMedium>
            {!isNetchexLogin && (
              <Flex gap={1} alignItems="center">
                <a
                  href={KH_LINKS.PARTIAL_PUBLISH_SHARE_RESULTS.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={sendKBaseEvent}
                >
                  <TextBodyMedium color="functional.1565BC">
                    {KH_LINKS.PARTIAL_PUBLISH_SHARE_RESULTS.description}
                  </TextBodyMedium>
                </a>
              </Flex>
            )}
          </Flex>
        </Flex>
      </DrawerHeaderSecondary>
      <DrawerBody>
        {isLoading && <Loading />}
        {!isLoading && (publishSuccessful ? <SuccessScreen /> : <FailureScreen />)}
      </DrawerBody>
      <DrawerFooter>
        <Button w="94px" variant="filled" colorScheme="blue" onClick={handleClick} disabled={isLoading}>
          {publishSuccessful ? 'Done' : 'Try Again'}
        </Button>
      </DrawerFooter>
    </>
  );
};

export default Result;
